import { useState, useEffect } from 'react'
import api from '../../services/api'
import { useTranslation } from 'react-i18next'

/** HR People Ops (v3.4) — leave approvals, review cycles, and the org chart. HR/Admin. */
export default function HRPeopleOps() {
  const { t } = useTranslation('ops')
  const [tab, setTab] = useState('leave')
  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('people_operations')}</h1>
        <p className="text-gray-500 text-sm mt-1">{t('hr_subtitle')}</p>
      </div>
      <div className="flex gap-2 border-b border-gray-200">
        {[['leave', 'Leave approvals'], ['reviews', 'Review cycles'], ['org', 'Org chart']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === id ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'leave' && <LeaveApprovals />}
      {tab === 'reviews' && <ReviewCycles />}
      {tab === 'org' && <OrgChart />}
    </div>
  )
}

const _DECIDED_PILL = {
  Approved: 'bg-emerald-100 text-emerald-700', Rejected: 'bg-red-100 text-red-700',
  Cancelled: 'bg-gray-100 text-gray-500',
}

function LeaveApprovals() {
  const { t } = useTranslation('ops')
  const [rows, setRows] = useState([])
  const [busy, setBusy] = useState(null)
  // Fetch ALL requests (no status filter) so we can show both the actionable
  // pending queue AND a history of past decisions (with their reason/note).
  const load = () => api.get('/hr/leave/requests').then(r => setRows(r.data || [])).catch(() => {})
  useEffect(() => { load() }, [])   // wrap: load() returns a Promise; an effect must return a cleanup fn or nothing
  const decide = async (id, approve) => {
    const note = approve
      ? (window.prompt('Optional note for this approval:') ?? '')
      : (window.prompt('Reason for rejection (optional):') ?? '')
    setBusy(id)
    try { await api.put(`/hr/leave/requests/${id}/decision`, { approve, note }); load() }
    catch (e) { alert(e.response?.data?.detail || 'Could not record the decision.') }
    finally { setBusy(null) }
  }
  const pending = rows.filter(r => r.status === 'Pending')
  const decided = rows.filter(r => r.status !== 'Pending')
  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-3">{t('pending_leave')}</h3>
        {pending.length === 0 ? <p className="text-sm text-gray-400 italic">{t('nothing_awaiting')}</p> : (
          <div className="divide-y divide-gray-100">
            {pending.map(r => (
              <div key={r.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800">{r.user_name || `User #${r.user_id}`} · {r.leave_type} · {r.days}d</p>
                  <p className="text-xs text-gray-500">{r.start_date} → {r.end_date}{r.reason ? ` · ${r.reason}` : ''}</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => decide(r.id, true)} disabled={busy === r.id}
                    className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-3 py-1.5 disabled:opacity-50">{t('approve')}</button>
                  <button onClick={() => decide(r.id, false)} disabled={busy === r.id}
                    className="text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 rounded-lg px-3 py-1.5">{t('reject')}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* History — decided/cancelled requests, kept with their reason & decision note. */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 mb-3">{t('decision_history')}</h3>
        {decided.length === 0 ? <p className="text-sm text-gray-400 italic">{t('no_decisions')}</p> : (
          <div className="divide-y divide-gray-100">
            {decided.map(r => (
              <div key={r.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800">{r.user_name || `User #${r.user_id}`} · {r.leave_type} · {r.days}d</p>
                  <p className="text-xs text-gray-500">{r.start_date} → {r.end_date}{r.reason ? ` · ${r.reason}` : ''}</p>
                  {r.decision_note && <p className="text-[11px] text-gray-500 italic">Note: {r.decision_note}</p>}
                  {r.decided_at && <p className="text-[11px] text-gray-400">Decided {String(r.decided_at).slice(0, 10)}</p>}
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${_DECIDED_PILL[r.status] || 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function ReviewCycles() {
  const { t } = useTranslation('ops')
  const [rows, setRows] = useState([])
  const [name, setName] = useState('')
  const [range, setRange] = useState({ period_start: '', period_end: '', due_date: '' })
  const load = () => api.get('/hr/reviews/cycles').then(r => setRows(r.data || [])).catch(() => {})
  useEffect(() => { load() }, [])   // wrap: load() returns a Promise; an effect must return a cleanup fn or nothing
  const create = async () => {
    if (!name.trim()) return
    try { await api.post('/hr/reviews/cycles', { name, ...range }); setName(''); setRange({ period_start: '', period_end: '', due_date: '' }); load() }
    catch (e) { alert(e.response?.data?.detail || 'Could not create the cycle.') }
  }
  const close = async (id) => { try { await api.put(`/hr/reviews/cycles/${id}/close`); load() } catch { /* ignore */ } }
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <h3 className="font-semibold text-gray-900 mb-3">{t('review_cycles')}</h3>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <input value={name} onChange={e => setName(e.target.value)} placeholder={t('ph_cycle_name')}
          className="flex-1 min-w-[180px] border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        <input type="date" title={t('t_period_start')} value={range.period_start} onChange={e => setRange({ ...range, period_start: e.target.value })} className="border border-gray-300 rounded-lg px-2 py-2 text-sm" />
        <input type="date" title={t('t_period_end')} value={range.period_end} onChange={e => setRange({ ...range, period_end: e.target.value })} className="border border-gray-300 rounded-lg px-2 py-2 text-sm" />
        <button onClick={create} className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700">+ New cycle</button>
      </div>
      {rows.length === 0 ? <p className="text-sm text-gray-400 italic">{t('no_review_cycles')}</p> : (
        <div className="divide-y divide-gray-100">
          {rows.map(rc => (
            <div key={rc.id} className="flex items-center justify-between gap-3 py-2.5">
              <div>
                <p className="text-sm font-medium text-gray-800">{rc.name}</p>
                <p className="text-xs text-gray-500">{rc.period_start || '—'} → {rc.period_end || '—'} · {rc.evaluations} evaluation(s)</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${rc.status === 'Open' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{rc.status}</span>
                {rc.status === 'Open' && <button onClick={() => close(rc.id)} className="text-[11px] text-gray-500 hover:text-gray-800">{t('close')}</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function OrgChart() {
  const { t } = useTranslation('ops')
  const [nodes, setNodes] = useState([])
  useEffect(() => { api.get('/hr/org').then(r => setNodes(r.data?.nodes || [])).catch(() => {}) }, [])
  const byId = Object.fromEntries(nodes.map(n => [n.id, n]))
  const roots = nodes.filter(n => !n.manager_id || !byId[n.manager_id])
  const childrenOf = (id) => nodes.filter(n => n.manager_id === id)
  const Node = ({ n, depth }) => (
    <div style={{ marginLeft: depth * 18 }} className="py-1">
      <span className="text-sm text-gray-800">{depth > 0 ? '↳ ' : ''}{n.name}</span>
      <span className="text-[11px] text-gray-400 ml-2">{n.role}{n.department ? ` · ${n.department}` : ''}</span>
      {childrenOf(n.id).map(c => <Node key={c.id} n={c} depth={depth + 1} />)}
    </div>
  )
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <h3 className="font-semibold text-gray-900 mb-3">{t('org_chart')}</h3>
      {nodes.length === 0 ? <p className="text-sm text-gray-400 italic">{t('no_people')}</p>
        : <div>{roots.map(r => <Node key={r.id} n={r} depth={0} />)}</div>}
      <p className="text-[11px] text-gray-400 mt-3">Reporting lines come from each person's manager. Set a manager on a user to build the tree.</p>
    </div>
  )
}
